import { Check, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import type { OrgCompany } from "@/api/diagnostic-org-structure";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Até quantas empresas o botão lista pelo nome; passando disso, mostra só a contagem. */
const MAX_LISTED = 2;

function companyLabel(company: OrgCompany): string {
  return company.name ? `${company.id} · ${company.name}` : company.id;
}

/**
 * O filtro de empresas da estrutura organizacional (spec 2026-10-23, D42): um
 * select de várias escolhas no padrão do filtro do agente de configuração. O botão
 * diz "Todas as empresas" (nada marcado) ou o que foi marcado; o popover traz a
 * busca por código ou nome, a lista com caixinhas e o "Limpar". Escolher não fecha
 * o popover, para marcar várias de uma vez.
 */
export function OrgCompanySelect({
  companies,
  selected,
  onChange,
}: {
  companies: OrgCompany[];
  selected: readonly string[];
  onChange(next: string[]): void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const chosen = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const marked = companies.filter((company) => chosen.has(company.id));
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? companies.filter(
        (company) =>
          company.id.toLowerCase().includes(needle) ||
          (company.name ?? "").toLowerCase().includes(needle),
      )
    : companies;

  const text = marked.map(companyLabel).join(", ");
  const triggerLabel =
    marked.length === 0
      ? t("All companies")
      : marked.length > MAX_LISTED
        ? t("{{total}} companies selected", { total: marked.length })
        : text;

  function toggle(id: string) {
    onChange(chosen.has(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  }

  function toggleFromKeyboard(event: KeyboardEvent<HTMLButtonElement>, id: string) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle(id);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          // O papel combobox não tira o nome do conteúdo; sem isto o botão ficaria sem nome.
          aria-label={triggerLabel}
          className="flex h-8 min-w-[220px] max-w-[420px] items-center justify-between gap-2 rounded-md border border-[#d4dae3] bg-white px-2 text-left text-xs text-[#3c4b61] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
        >
          <span className="truncate" title={text || undefined}>
            {triggerLabel}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-2">
        <Input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("Search code or name…")}
          aria-label={t("Search code or name…")}
          className="h-8 text-[13px]"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => onChange([])}
            className="text-xs text-[#596b85] underline-offset-2 hover:text-[#08254c] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            {t("Clear")}
          </button>
        </div>
        <div className="mt-1 max-h-72 overflow-y-auto">
          {visible.length === 0 ? (
            <p className="px-2 py-3 text-[12px] text-[#596b85]">{t("No company found")}</p>
          ) : (
            visible.map((company) => {
              const checked = chosen.has(company.id);
              return (
                <button
                  key={company.item_key}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => toggle(company.id)}
                  onKeyDown={(event) => toggleFromKeyboard(event, company.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-[#3c4b61] hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                      checked
                        ? "border-[#08254c] bg-[#08254c] text-white"
                        : "border-[#cfd6e2] bg-white text-transparent",
                    )}
                  >
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="truncate">{companyLabel(company)}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
